import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { formatDateTime, formatNumber } from "../lib/utils";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";

interface ChatRow {
  id: string;
  telegram_chat_id: string;
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  status: string;
  message_count: number;
  last_message_at: string | null;
  created_at: string;
  linked_user_id: string | null;
  linked_user_email: string | null;
}

interface ChatsResponse {
  chats: ChatRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface ChatStats {
  totalCount: number;
  activeCount: number;
  newToday: number;
  totalMessages: number;
}

function getStatusBadge(status: string) {
  const map: Record<string, { label: string; className: string }> = {
    active: { label: "Активен", className: "bg-green-100 text-green-700" },
    closed: { label: "Закрыт", className: "bg-gray-100 text-gray-600" },
  };
  const item = map[status] || { label: status, className: "bg-gray-100 text-gray-600" };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${item.className}`}>
      {item.label}
    </span>
  );
}

function getDisplayName(chat: ChatRow): string {
  const parts = [chat.first_name, chat.last_name].filter(Boolean);
  if (parts.length > 0) return parts.join(" ");
  if (chat.username) return `@${chat.username}`;
  return chat.telegram_chat_id;
}

export default function ChatsPage() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const limit = 20;

  const searchQuery = search.length >= 2 ? search : "";

  const { data, isLoading } = useQuery<ChatsResponse>({
    queryKey: [
      `/api/admin/system-chats?page=${page}&limit=${limit}${status ? `&status=${status}` : ""}${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ""}&sort=last_message_at&order=desc`,
    ],
  });

  const { data: statsData } = useQuery<ChatStats>({
    queryKey: ["/api/admin/system-chats/stats"],
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Чаты AI-сотрудника</h1>
        {data && (
          <span className="text-sm text-muted-foreground">
            Всего: {formatNumber(data.total)}
          </span>
        )}
      </div>

      {/* Stats Cards */}
      {statsData && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="text-sm text-muted-foreground">Всего чатов</div>
            <div className="text-2xl font-bold">{statsData.totalCount}</div>
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="text-sm text-muted-foreground">Активных</div>
            <div className="text-2xl font-bold">{statsData.activeCount}</div>
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="text-sm text-muted-foreground">Новых сегодня</div>
            <div className="text-2xl font-bold">{statsData.newToday}</div>
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <div className="text-sm text-muted-foreground">Всего сообщений</div>
            <div className="text-2xl font-bold">{formatNumber(statsData.totalMessages)}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Поиск по имени, username, email..."
            className="pl-9 pr-4 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring w-72"
          />
        </div>
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 rounded-md border border-input bg-background text-sm"
        >
          <option value="">Все статусы</option>
          <option value="active">Активные</option>
          <option value="closed">Закрытые</option>
        </select>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Пользователь
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Аккаунт
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Сообщ.
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Статус
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Посл. сообщение
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                  Создан
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 bg-muted rounded w-20 animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : data?.chats.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-muted-foreground"
                  >
                    Чаты не найдены
                  </td>
                </tr>
              ) : (
                data?.chats.map((c) => (
                  <tr key={c.id} className="hover:bg-muted/30">
                    <td className="px-4 py-2">
                      <Link href={`/chats/${c.id}`}>
                        <span className="text-sm text-primary hover:underline cursor-pointer font-medium">
                          {getDisplayName(c)}
                        </span>
                      </Link>
                      {c.username && c.first_name && (
                        <div className="text-xs text-muted-foreground">@{c.username}</div>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {c.linked_user_id ? (
                        <Link href={`/users/${c.linked_user_id}`}>
                          <span className="text-sm text-primary hover:underline cursor-pointer">
                            {c.linked_user_email}
                          </span>
                        </Link>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-sm text-center">
                      {c.message_count}
                    </td>
                    <td className="px-4 py-2">{getStatusBadge(c.status)}</td>
                    <td className="px-4 py-2 text-sm text-muted-foreground">
                      {c.last_message_at ? formatDateTime(c.last_message_at) : "—"}
                    </td>
                    <td className="px-4 py-2 text-sm text-muted-foreground">
                      {formatDateTime(c.created_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            Стр. {data.page} из {data.totalPages}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 rounded-md border border-input text-sm disabled:opacity-50 hover:bg-muted"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() =>
                setPage((p) => Math.min(data.totalPages, p + 1))
              }
              disabled={page >= data.totalPages}
              className="px-3 py-1.5 rounded-md border border-input text-sm disabled:opacity-50 hover:bg-muted"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
